import React from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { FaUserCircle, FaSnowflake, FaGift, FaCoins, FaInfoCircle } from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";

const HowItWorks = () => {
  const bonusSummary = [
    { range: "14 – 26", referrals: 13, total: 13 * 50, level: "Bonus Lvl 1" },
    { range: "352 – 702", referrals: 351, total: 351 * 50, level: "Bonus Lvl 2" },
    { range: "6562 – 13122", referrals: 6561, total: 6561 * 50, level: "Bonus Lvl 3" },
  ];

  return (
    <section className="bg-primary-100 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header Section */}
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-black text-white flex items-center justify-center gap-3">
            <span className="p-2 bg-secondary rounded-xl text-primary transform rotate-12">
              <FaInfoCircle size={20} />
            </span>
            How This Works?
          </h3>
          <p className="text-gray-400 text-sm font-medium">Understand your earning potential and chain structure</p>
        </div>

        {/* Visual Referral Tree */}
        <div className="bg-primary rounded-3xl p-8 shadow-2xl border border-white/5 overflow-x-auto no-scrollbar">
          <h4 className="text-secondary text-[11px] font-black uppercase tracking-widest mb-10 text-center">Referral Structure (Example)</h4>
          <div className="min-w-[300px]">
            <Tree
              lineWidth={"2px"}
              lineColor={"#ffcc00"}
              lineBorderRadius={"15px"}
              label={
                <div className="inline-block p-4 rounded-2xl bg-secondary text-primary shadow-xl transform transition-transform hover:scale-105">
                  <FaUserCircle size={32} className="mx-auto mb-2" />
                  <span className="font-black text-xs uppercase">YOU</span>
                </div>
              }
            >
              <TreeNode label={
                <div className="inline-block p-3 rounded-xl bg-white/5 border border-white/10 text-white mt-4 transition-all hover:bg-white/10">
                  <p className="text-[10px] font-black text-secondary">CHAIN A</p>
                  <p className="text-[9px] text-gray-400">1st Referral</p>
                </div>
              } />
              <TreeNode label={
                <div className="inline-block p-3 rounded-xl bg-white/5 border border-white/10 text-white mt-4 transition-all hover:bg-white/10">
                  <p className="text-[10px] font-black text-secondary">CHAIN B</p>
                  <p className="text-[9px] text-gray-400">2nd Referral</p>
                </div>
              } />
              <TreeNode label={
                <div className="inline-block p-3 rounded-xl bg-white/5 border border-white/10 text-white mt-4 transition-all hover:bg-white/10">
                  <p className="text-[10px] font-black text-secondary">CHAIN C</p>
                  <p className="text-[9px] text-gray-400">3rd Referral</p>
                </div>
              } />
            </Tree>
          </div>
          <p className="text-center text-[10px] text-gray-500 mt-8 italic">
            Each of these three chains grows independently to unlock bonuses.
          </p>
        </div>

        {/* Bonus Timeline / Levels */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-bold text-lg flex items-center gap-2">
              <span className="w-1 h-6 bg-secondary rounded-full"></span>
              Bonus Progression
            </h4>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Per Chain Logic</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Level 1: Cooling */}
            <div className="bg-primary p-5 rounded-3xl border border-white/5 shadow-xl relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-blue-500/10 rounded-full blur-xl transition-all group-hover:scale-150"></div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500/20 text-blue-400 rounded-2xl">
                  <FaSnowflake size={24} className="animate-spin-slow" />
                </div>
                <div>
                  <h5 className="text-white font-black text-sm uppercase">Cooling Period</h5>
                  <p className="text-blue-400 text-2xl font-black mt-1">1 – 13</p>
                  <p className="text-gray-500 text-xs mt-1 font-medium">Initial referrals per chain. No bonus during this phase.</p>
                </div>
              </div>
            </div>

            {/* Level 2: Bonus 1 */}
            <div className="bg-primary p-5 rounded-3xl border border-secondary/20 shadow-xl relative overflow-hidden group">
              <div className="absolute -right-4 -top-4 w-20 h-20 bg-secondary/10 rounded-full blur-xl transition-all group-hover:scale-150"></div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-secondary/20 text-secondary rounded-2xl">
                  <FaGift size={24} />
                </div>
                <div>
                  <h5 className="text-white font-black text-sm uppercase">Bonus Level 1</h5>
                  <p className="text-secondary text-2xl font-black mt-1">14 – 26</p>
                  <p className="text-gray-400 text-xs mt-1 font-medium">Get <span className="text-white font-bold">₹50</span> for every user in this range!</p>
                </div>
              </div>
            </div>

            {/* Level 3: Cooling */}
            <div className="bg-primary p-5 rounded-3xl border border-white/5 shadow-xl relative overflow-hidden opacity-80">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-gray-500/20 text-gray-400 rounded-2xl">
                  <FaSnowflake size={24} />
                </div>
                <div>
                  <h5 className="text-white font-black text-sm uppercase">Cooling Period</h5>
                  <p className="text-gray-400 text-2xl font-black mt-1">27 – 351</p>
                </div>
              </div>
            </div>

            {/* Level 4: Bonus 2 */}
            <div className="bg-primary p-5 rounded-3xl border border-secondary/20 shadow-xl relative overflow-hidden group">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-secondary/20 text-secondary rounded-2xl">
                  <MdTrendingUp size={24} />
                </div>
                <div>
                  <h5 className="text-white font-black text-sm uppercase">Bonus Level 2</h5>
                  <p className="text-secondary text-2xl font-black mt-1">352 – 702</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <div className="bg-secondary rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden text-primary">
          <FaCoins className="absolute -right-8 -bottom-8 text-primary/10 text-9xl -rotate-12" />
          
          <div className="relative z-10 space-y-6">
            <h4 className="text-xl font-black uppercase tracking-tight text-center">Total Potential Earnings</h4>
            
            <div className="grid grid-cols-1 gap-4">
              {bonusSummary.map((b, i) => (
                <div key={i} className="flex items-center justify-between border-b border-primary/10 pb-4 last:border-0 last:pb-0">
                  <div>
                    <p className="text-[10px] font-black uppercase opacity-60">{b.level}</p>
                    <p className="font-bold text-sm">Range: {b.range} </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black uppercase opacity-60">Earnings</p>
                    <p className="font-black text-lg">₹{b.total.toLocaleString()} X 3<span className="text-[10px]">/ Chain</span></p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-primary/10 flex flex-col items-center gap-2">
              <p className="text-xs font-bold uppercase opacity-60 tracking-widest">Total for all 3 chains</p>
              <p className="text-4xl font-black tracking-tighter">₹1,038,750</p>
              <p className="text-[10px] font-black bg-primary text-white px-3 py-1 rounded-full mt-2">1 COIN = 1 ₹</p>
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-500 font-medium px-8 leading-relaxed">
          The Winner India Marketing Platform uses a structured referral program designed for long-term growth and high rewards. 
          Focus on building balanced chains to maximize your bonus potential.
        </p>
      </div>
    </section>
  );
};

export default HowItWorks;
